// =============================================================================
// packhouseConfigHref — the URL of one tab of the packhouse Config page.
//
// Packhouse screens name a setting's home as text ("Config → Storage → Cooling
// flow"), and the person then has to find Config and the tab by hand. /config
// already opens the tab named in `?tab=` (config/index.tsx), so a pointer can
// be a link — this builds it, and <ConfigLink/> renders it.
//
// The tab list lives HERE and the Config page imports it, so this helper can
// never produce a tab the page does not read: a `?tab=` the page ignores lands
// on whichever tab was open last, which reads as a broken link.
//
// `section` is the setting the pointer names inside the tab. A section listed
// in SECTION_ANCHORS has an address — `&section=<id>` — and the Config page
// opens that section and scrolls to it (its <Collapsible id>). A section with
// no entry links to its TAB, and the reader scrolls from there (F19 UF-01).
// =============================================================================

export const PACKHOUSE_CONFIG_TABS = [
  'business', 'operations', 'dispatch', 'storage', 'commodities', 'health', 'admin',
] as const

export type PackhouseConfigTab = typeof PACKHOUSE_CONFIG_TABS[number]

/** The tab a `?tab=` value or a saved tab names. 'ppecb' is the old name of the
 *  Business tab (U27 D1), kept so a saved link or remembered tab still lands. */
export function normaliseConfigTab(v: string | null | undefined): PackhouseConfigTab | null {
  if (v === 'ppecb') return 'business'
  return isPackhouseConfigTab(v) ? v : null
}

export function isPackhouseConfigTab(v: string | null | undefined): v is PackhouseConfigTab {
  return !!v && (PACKHOUSE_CONFIG_TABS as readonly string[]).includes(v)
}

/** Section name (as the pointer prints it) → the section's id on the Config
 *  page. An id here must match a `<Collapsible id>` on that tab, or the link
 *  lands on the tab with nothing opened (the test pins both sides). */
export const SECTION_ANCHORS: { readonly [T in PackhouseConfigTab]?: Readonly<Record<string, string>> } = {
  business: {
    'Letterhead / branding':     'letterhead',
    'PPECB identity & defaults': 'ppecb-identity',
  },
  dispatch: {
    'Destinations':   'customers',
    // The Find-a-setting title for the same section (F19 UF-26 renamed it).
    'Customers':      'customers',
    'Exporters':      'exporters',
    'Transporters':   'transporters',
    'Target markets': 'target-markets',
  },
  operations: {
    'Line presets':      'line-presets',
    'Packing products':  'packing-products',
    'Pack lines':        'pack-lines',
    'Pack rates':        'pack-rates',
    'Shrinkage valuation': 'shrinkage-valuation',
    'Labour defaults':   'labour-defaults',
  },
  storage: {
    'Coldrooms':         'coldrooms',
    // Pointers name it "Cooling flow"; its header reads "Cooling flow → rooms".
    'Cooling flow':      'cooling-flow',
    'Cooling flow → rooms': 'cooling-flow',
    'Cold-storage rental': 'cold-storage-rental',
    'Bin fleet':         'bin-fleet',
    'Cooling defaults':  'cooling-defaults',
    'Loading Bay Lanes': 'loading-bay-lanes',
    // F08 UF-03: the bin-type list, moved from Bin Inventory.
    'Bin types':         'bin-types',
  },
  // The Setup tab (key 'health' — its old name, kept so saved links and the
  // remembered tab still land): the first-day steps above or below the checks.
  health: {
    'Getting started': 'getting-started',
  },
  // An always-open card, so a <ConfigSectionAnchor id> rather than a
  // <Collapsible id>: Benchmarks' "Enable in Settings" lands on it (UF-10).
  admin: {
    'Industry benchmarks': 'data-sharing',
    'Email groups':      'email-groups',
  },
}

export function packhouseConfigHref(tab: PackhouseConfigTab, section?: string): string {
  const anchor = section ? SECTION_ANCHORS[tab]?.[section] : undefined
  return `/config?tab=${tab}${anchor ? `&section=${anchor}` : ''}`
}
